import t from "@/lang/lang";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import type { MonitorAdditionalConfigDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { normalizeMonitorAdditionalConfig } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";

const tooltipContentClassName =
  "bg-background text-foreground max-w-sm border text-left text-xs [&>svg]:bg-background [&>svg]:fill-background";

export function AdditionalConfigForm({
  config,
  disabled = false,
  onChange,
}: {
  config?: Partial<MonitorAdditionalConfigDTO>;
  disabled?: boolean;
  onChange: (config: MonitorAdditionalConfigDTO) => void;
}) {
  const normalizedConfig = normalizeMonitorAdditionalConfig(config);
  const updateConfig = (patch: Partial<MonitorAdditionalConfigDTO>) => {
    const nextConfig = normalizeMonitorAdditionalConfig({
      ...normalizedConfig,
      ...patch,
    });
    if (patch.includeInRentingCalculation === true) {
      nextConfig.includeInMaterialCalculation = false;
    }
    if (patch.includeInMaterialCalculation === true) {
      nextConfig.includeInRentingCalculation = false;
    }
    onChange(nextConfig);
  };

  return (
    <div className="rounded-md border p-4">
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(18rem,22rem)]">
        <div className="space-y-3">
          <div>
            <div className="text-sm font-medium">{t("Berechnung")}</div>
            <div className="text-muted-foreground text-xs">
              {t(
                "Legen Sie fest, in welchen Kalkulationsbereichen dieser Zusatz berücksichtigt wird.",
              )}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <AdditionalConfigCheckbox
              checked={normalizedConfig.includeInMaterialCalculation}
              disabled={disabled}
              label={t("Materialkosten")}
              info={t(
                'Wenn aktiv, wird dieser Zusatz als "Anschaffung von Boxen- und Fallensystemen für das Quality Pest Management QPM®" gelistet und in der Materialkostenkalkulation verwendet. Wenn deaktiviert, wird er dort nicht angezeigt und nicht berechnet.',
              )}
              onCheckedChange={(checked) =>
                updateConfig({ includeInMaterialCalculation: checked })
              }
            />
            <AdditionalConfigCheckbox
              checked={normalizedConfig.includeInRentingCalculation}
              disabled={disabled}
              label={t("Mietpreis verwenden")}
              info={t(
                "Wenn aktiv, wird der Anschaffungspreis nicht als Materialkostenposition verwendet. Stattdessen werden die Mietpreise dieses Zusatzes in der IoT-Mietkalkulation berücksichtigt.",
              )}
              onCheckedChange={(checked) =>
                updateConfig({ includeInRentingCalculation: checked })
              }
            />
            <AdditionalConfigCheckbox
              checked={normalizedConfig.includeInInstallationCalculation}
              disabled={disabled}
              label={t("Einrichtungskosten")}
              onCheckedChange={(checked) =>
                updateConfig({ includeInInstallationCalculation: checked })
              }
            />
            <AdditionalConfigCheckbox
              checked={normalizedConfig.includeInServiceCalculation}
              disabled={disabled}
              label={t("Servicekosten")}
              onCheckedChange={(checked) =>
                updateConfig({ includeInServiceCalculation: checked })
              }
            />
            <AdditionalConfigCheckbox
              checked={normalizedConfig.showInOfferMonitorCatalog}
              disabled={disabled}
              label={t("Im Angebotskatalog anzeigen")}
              onCheckedChange={(checked) =>
                updateConfig({ showInOfferMonitorCatalog: checked })
              }
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label>{t("Auswahl in der Bestandsaufnahme")}</Label>
          <Select
            value={normalizedConfig.selectionMode}
            disabled={disabled}
            onValueChange={(selectionMode) =>
              updateConfig({
                selectionMode:
                  selectionMode as MonitorAdditionalConfigDTO["selectionMode"],
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALWAYS_REQUIRED">
                {t("Immer erforderlich")}
              </SelectItem>
              <SelectItem value="OPTIONAL">{t("Optional")}</SelectItem>
              <SelectItem value="DEFAULT_SELECTED_OPTIONAL">
                {t("Standardmäßig ausgewählt")}
              </SelectItem>
            </SelectContent>
          </Select>
          <div className="text-muted-foreground text-xs leading-5">
            {t(
              "Immer erforderliche Zusätze werden automatisch gesetzt. Standardmäßig ausgewählte Zusätze können in der Bestandsaufnahme abgewählt werden.",
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function AdditionalConfigCheckbox({
  checked,
  disabled,
  label,
  info,
  onCheckedChange,
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  info?: string;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox
        checked={checked}
        disabled={disabled}
        onCheckedChange={(nextChecked) => onCheckedChange(nextChecked === true)}
      />
      <span>{label}</span>
      {info ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Info className="text-muted-foreground h-4 w-4" />
            </TooltipTrigger>
            <TooltipContent className={tooltipContentClassName}>
              <p>{info}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : null}
    </label>
  );
}
